import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Eye, Inbox } from "lucide-react";
import { withTimeout } from "@/hooks/useLoadState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  Complete: { bg: "#E4F3EC", color: "#1A6B42" },
  "In Progress": { bg: "#FEF3DC", color: "#C97B1A" },
  Draft: { bg: "#F1F1EF", color: "#9499A8" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

const fmtScore = (s, mode) => {
  if (s === null || s === undefined || isNaN(Number(s))) return "—";
  const denom = (mode || "").toLowerCase() === "growth" ? "5.0" : "4.0";
  return `${Number(s).toFixed(2)} / ${denom}`;
};

export default function AssessmentTemplates({ session }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Legacy deep-link safety: ?take=<sessionId> should run the assessment.
  // After the iter-72 route swap, AssessmentHub lives at /app/assessment-templates.
  useEffect(() => {
    const take = searchParams.get("take");
    if (take) {
      navigate(`/app/assessment-templates?take=${encodeURIComponent(take)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const loadResults = useCallback(() => {
    const headers = {
      "x-tenant-id": session?.tenant?.id || "tenant_1",
      "x-user-id": session?.user?.id || "admin",
      "x-user-role": session?.user?.role || "admin",
    };
    setLoading(true);
    setLoadErr(null);
    withTimeout(fetch(`${BACKEND_URL}/api/assessments/results-list`, { headers }).then((r) => {
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      return r.json();
    }))
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setLoadErr(e?.message || "Failed to load assessments"))
      .finally(() => setLoading(false));
  }, [session]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter !== "All") out = out.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => (r.organization || "").toLowerCase().includes(q));
    }
    return out;
  }, [rows, statusFilter, search]);

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="assessment-templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-[26px] font-bold tracking-tight" style={{ color: "#0F1117" }}>
            Assessments
          </h1>
          <p className="text-[14px] mt-1" style={{ color: "#5C6070" }}>
            Completed customer assessments — flat list across all organizations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#9499A8" }} />
            <input
              type="text"
              placeholder="Filter by organization…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-[10px] border bg-white text-[13px] outline-none focus:border-[#3B4FE4]"
              style={{ borderColor: "rgba(0,0,0,0.08)", color: "#0F1117", width: 260 }}
              data-testid="assessment-templates-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-[10px] border bg-white text-[13px] outline-none focus:border-[#3B4FE4]"
            style={{ borderColor: "rgba(0,0,0,0.08)", color: "#0F1117" }}
            data-testid="assessment-templates-status-filter"
          >
            <option value="All">All statuses</option>
            <option value="Complete">Complete</option>
            <option value="In Progress">In Progress</option>
            <option value="Draft">Draft</option>
          </select>
        </div>
      </div>

      <div
        className="rounded-2xl border bg-white overflow-hidden"
        style={{ borderColor: "rgba(0,0,0,0.08)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]" data-testid="assessment-templates-table">
            <thead style={{ background: "#FAFAF8", color: "#5C6070" }}>
              <tr className="text-[11px] font-semibold tracking-[0.06em] uppercase">
                <th className="px-5 py-3">Organization</th>
                <th className="px-5 py-3">Template</th>
                <th className="px-5 py-3">Completed</th>
                <th className="px-5 py-3">Score</th>
                <th className="px-5 py-3">Consultant</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-[13px]" style={{ color: "#4b5563" }} aria-busy="true">
                    Loading completed assessments…
                  </td>
                </tr>
              )}
              {!loading && loadErr && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center" role="alert" data-testid="assessment-templates-error">
                    <div className="text-[14px] font-semibold text-[#991b1b]">Failed to load assessments</div>
                    <div className="text-[13px] text-[#4b5563] mt-1">{loadErr}</div>
                    <button onClick={loadResults} className="mt-3 h-9 px-4 rounded-lg border border-[#e5e7eb] bg-white text-[13px] font-semibold text-[#111827] hover:bg-[#f9fafb]" data-testid="assessment-templates-retry">Retry</button>
                  </td>
                </tr>
              )}
              {!loading && !loadErr && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center" data-testid="assessment-templates-empty">
                    <Inbox className="h-10 w-10 mx-auto mb-3" style={{ color: "#9499A8" }} />
                    <div className="text-[14px] font-semibold" style={{ color: "#0F1117" }}>
                      No completed assessments yet
                    </div>
                    <div className="text-[13px] mt-1" style={{ color: "#5C6070" }}>
                      Assessments will appear here once they have been submitted and scored.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && !loadErr && filtered.map((r) => {
                const pill = STATUS_PILL[r.status] || STATUS_PILL.Draft;
                return (
                  <tr
                    key={r.id}
                    className="border-t transition-colors hover:bg-[#FAFAF8]"
                    style={{ borderColor: "rgba(0,0,0,0.06)" }}
                    data-testid={`assessment-templates-row-${r.id}`}
                  >
                    <td className="px-5 py-3 font-semibold" style={{ color: "#0F1117" }}>
                      <button
                        type="button"
                        onClick={() => r.tenant_id && navigate(`/app/tenants/${r.tenant_id}`)}
                        className="hover:underline text-left"
                      >
                        {r.organization || "—"}
                      </button>
                    </td>
                    <td className="px-5 py-3" style={{ color: "#2D3142" }}>{r.template_name || "—"}</td>
                    <td className="px-5 py-3 tabular-nums" style={{ color: "#2D3142" }}>{fmtDate(r.completed_at)}</td>
                    <td className="px-5 py-3 tabular-nums font-semibold" style={{ color: "#0F1117" }}>
                      {fmtScore(r.overall_score, r.scoring_mode)}
                    </td>
                    <td className="px-5 py-3" style={{ color: "#5C6070" }}>{r.consultant || "—"}</td>
                    <td className="px-5 py-3">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                        style={{ background: pill.bg, color: pill.color }}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/app/customers/${r.prospect_id}?tab=assessment`)}
                        className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[12px] font-semibold transition-colors hover:border-[#3B4FE4] hover:text-[#3B4FE4]"
                        style={{ borderColor: "rgba(0,0,0,0.08)", color: "#2D3142" }}
                        data-testid={`assessment-templates-view-${r.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
